import { ErrorCode, ErrorLevel, type AppError, type ApiErrorResponse } from './types';

/**
 * Factory class for creating standardized error objects
 */
export class ErrorFactory {
  /**
   * Creates a generic application error
   * @param message - Human-readable error message
   * @param code - Error code for programmatic handling
   * @param level - Error severity level
   * @returns Standardized AppError object
   */
  static create(
    message: string,
    code: ErrorCode | string = ErrorCode.UNKNOWN_ERROR,
    level: ErrorLevel = ErrorLevel.ERROR
  ): AppError {
    return {
      message,
      code,
      level,
      timestamp: Date.now()
    };
  }

  /**
   * Creates an error from RTK Query API response
   * @param error - Error object from RTK Query (fetchBaseQuery)
   * @returns Standardized AppError object
   */
  static fromApiError(error: unknown): AppError {
    if (this.isRtkQueryError(error)) {
      const data = error.data as Partial<ApiErrorResponse>;

      if (data?.message) {
        return {
          message: data.message,
          code: data.code || ErrorCode.UNKNOWN_ERROR,
          level: this.getErrorLevelFromCode(data.code),
          details: data,
          timestamp: Date.now()
        };
      }
    }

    const message = this.extractErrorMessage(error);
    return {
      message,
      code: ErrorCode.UNKNOWN_ERROR,
      level: ErrorLevel.ERROR,
      timestamp: Date.now()
    };
  }

  /**
   * Creates an authentication error
   * @param message - Custom error message
   * @param code - Specific auth error code
   * @returns Authentication error object
   */
  static authError(message = 'Authentication failed', code: ErrorCode = ErrorCode.AUTH_ERROR): AppError {
    return {
      message,
      code,
      level: ErrorLevel.ERROR,
      timestamp: Date.now()
    };
  }

  /**
   * Creates a validation error
   * @param message - Validation error message
   * @param details - Additional validation details
   * @returns Validation error object
   */
  static validationError(message: string, details?: unknown): AppError {
    return {
      message,
      code: ErrorCode.VALIDATION_ERROR,
      level: ErrorLevel.WARNING,
      details,
      timestamp: Date.now()
    };
  }

  /**
   * Creates a permission error
   * @param message - Custom permission error message
   * @returns Permission error object
   */
  static permissionError(message = 'You do not have permission to perform this action'): AppError {
    return {
      message,
      code: ErrorCode.PERMISSION_DENIED,
      level: ErrorLevel.ERROR,
      timestamp: Date.now()
    };
  }

  /**
   * Creates a not found error
   * @param message - Custom not found message
   * @returns Not found error object
   */
  static notFoundError(message = 'The requested resource was not found'): AppError {
    return {
      message,
      code: ErrorCode.RECORD_NOT_FOUND,
      level: ErrorLevel.WARNING,
      timestamp: Date.now()
    };
  }

  /**
   * Maps error codes to appropriate severity levels
   * @param code - Error code to map
   * @returns Appropriate error level
   */
  private static getErrorLevelFromCode(code?: string): ErrorLevel {
    if (!code) return ErrorLevel.ERROR;

    switch (code) {
      case ErrorCode.VALIDATION_ERROR:
      case ErrorCode.RECORD_NOT_FOUND:
        return ErrorLevel.WARNING;

      case ErrorCode.SESSION_EXPIRED:
      case ErrorCode.USER_EXPIRED:
      case ErrorCode.PERMISSION_DENIED:
        return ErrorLevel.ERROR;

      case ErrorCode.JWT_CONFIG_ERROR:
      case ErrorCode.NETWORK_ERROR:
        return ErrorLevel.CRITICAL;

      default:
        return ErrorLevel.ERROR;
    }
  }

  private static isRtkQueryError(error: unknown): error is { data: unknown; status: number } {
    return typeof error === 'object' && error !== null && 'data' in error && 'status' in error;
  }

  private static extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return 'An unexpected error occurred';
  }
}
